import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';

import { addGenericErrorToast } from 'core/notifications';

import {
  EmptyState,
  getDataFromDomElement,
  getIsCourseReadOnly,
  useGlobalQueries,
  useObjectiveableIdFromUrl,
  useSocketsSubscription,
} from '@/exceed';
import axios from '@/util/axios';

import AiGenerateButton from './aiGenerateButton';

import getAxiosConfig from '../helpers/getAxiosConfig';

import useLearningObjectives from '../hooks/useLearningObjectives';

import styles from '../styles/contentGeneration.module.scss';

const getAiGenerateButtonState = ({ learningObjectives }) => {
  const hasLearningObjectives = Boolean(
    learningObjectives.isSuccess && learningObjectives.data?.learningObjectives?.data?.length,
  );

  if (!hasLearningObjectives) {
    return {
      isDisabled: true,
      text: i18n.t('missing-learning-objectives'),
    };
  }

  return {
    isDisabled: false,
    text: null,
  };
};

const ContentGeneration = ({ draftState = 'editing', isCourseDraft = true, channelName }) => {
  const [intellumTutorAPI, setIntellumTutorAPI] = useState(null);
  const [intellumTutorStatus, setIntellumTutorStatus] = useState('closed');
  const objectiveableId = useObjectiveableIdFromUrl();

  const dataFromDomElement = getDataFromDomElement('data-course-ai-content-generation');
  const isCourseEmpty = dataFromDomElement.courseIsEmpty === 'true';
  const isGenerating = intellumTutorStatus === 'loading' || intellumTutorStatus === 'open';
  const isReadOnly = getIsCourseReadOnly({ draftState, isCourseDraft });
  const { courseCurriculumId } = dataFromDomElement;

  const { learningObjectives } = useLearningObjectives({
    draftState,
    isCourseDraft,
    objectiveableId,
    objectiveableParentId: courseCurriculumId,
  });
  const { currentProfile } = useGlobalQueries();

  useEffect(() => {
    window.IntellumTutor?.({
      key: 'content_generation',
      onLoad: setIntellumTutorAPI,
      onStatusChange: setIntellumTutorStatus,
      renderElement: document.getElementById('intellum_tutor_content_generation'),
    });
  }, []);

  useSocketsSubscription({
    channelName,
    events: {
      update: () => {
        const pageComponents = document.getElementById('page_components');
        if (pageComponents) {
          axios(getAxiosConfig('get-page-components', { id: objectiveableId }))
            .then((response) => {
              pageComponents.innerHTML = response.data;
              const emptyStateElement = document.querySelector('[data-blocktype="content-generation-empty-state"]');
              if (emptyStateElement) {
                emptyStateElement.style.display = 'none';
              }
            })
            .catch((error) => {
              addGenericErrorToast();
              console.error('Error fetching page components:', error);
            });
        }
      },
    },
  });

  if (!courseCurriculumId || !isCourseEmpty) {
    return null;
  }

  const handleAiGenerateClick = () => {
    intellumTutorAPI?.openChat({
      ai_model: 'content_generation_chat',
      ai_context: `Course::${objectiveableId},CourseCurriculum::${courseCurriculumId}`,
      exceed_user_id: currentProfile.data.id,
    });
  };

  const { isDisabled: isAiGenerateButtonDisabled, text: disabledAiGenerateButtonText } =
    getAiGenerateButtonState({ learningObjectives });

  return (
    <div className={styles.element}>
      <EmptyState
        actions={
          <AiGenerateButton
            className={styles.button}
            content={i18n.t('content-generation.suggest-content')}
            disabledText={disabledAiGenerateButtonText}
            isDisabled={isAiGenerateButtonDisabled}
            isGenerating={isGenerating}
            onClick={handleAiGenerateClick}
          />
        }
        body={i18n.t('content-generation.empty-state-body')}
        testId="content-generation-empty-state"
        title={
          isReadOnly
            ? i18n.t('content-generation.empty-state-title-read-only')
            : i18n.t('content-generation.empty-state-title')
        }
        containerProps={{
          'data-blocktype': 'content-generation-empty-state',
        }}
      />
    </div>
  );
};

ContentGeneration.propTypes = {
  draftState: PropTypes.oneOf([null, 'editing', 'in_review', 'approved']),
  isCourseDraft: PropTypes.bool,
};

export default ContentGeneration;
